import React, {ElementType, useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {Chip, Typography} from "@mui/material";
import {useGridApiRef, GridColumns} from "@mui/x-data-grid-pro";
import {GridEnrichedColDef} from "@mui/x-data-grid/models/colDef/gridColDef";
import BlockedTemplateCreationDialog from "../../patterns/dialog/blockedTemplateCreationDialog";
import PremiumFeaturesBox from "../../components/premium/premiumFeaturesBox";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PremiumBadge from "../../components/premium/premiumBadge";
import {isSuperAdmin} from "../../common/struct/globalVar";
import EmailIcon from '@mui/icons-material/Email';
import VisibilityIcon from "@mui/icons-material/Visibility";
import InventoryIcon from "@mui/icons-material/Inventory";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SettingsIcon from '@mui/icons-material/Settings';
import HistoryIcon from "@mui/icons-material/History";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import List from "../../patterns/list/list";
import AddIcon from '@mui/icons-material/Add';
import SendIcon from "@mui/icons-material/Send";
import TimelapseIcon from '@mui/icons-material/Timelapse';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import {PRIVATE_URL, operationUrl} from "../../common/struct/urlManager";
import {TAlert} from "../../patterns/list/listSnackbar";
import {actionColumn, addErrorAlert, addSuccessAlert, columnDefinition, ColumnType, gridAction} from "../../patterns/list/listUtils";
import DeleteDialog from "../../patterns/dialog/deleteDialog";
import RestoreDialog from "../../patterns/dialog/restoreDialog";
import {TEmailStats, TEmailTemplate, TEmailTemplateType} from "../../interfaces/email";
import {TOperation} from "../../interfaces/operation";
import {createEmailTemplate, deleteEmailTemplate, getEmailTemplates, resetInvitationEmailTemplate, sendTestEmailForTemplate} from "../../services/AdminService";
import {manageStringError, RequestFilter, RequestOrder, RequestFilterOperator} from "../../common/methods/ApiService";
import {getExportDocument} from "../../common/methods/exportDocument";
import emptyTransactions from "../../assets/lists/empty_transactions.svg";
import {getOperationEmailStats, getExportOperationEmailStats} from "../../services/OperationService";
import Panel, {PanelSize} from "../../patterns/panel/panel";
import EmailTemplatePreviewPanel from "./emailTemplatePreviewPanel";
import ListMenuItem from "../../patterns/list/listMenuItem";
import ListArchivedEmailTemplate from "./archived/listArchivedEmailTemplates";
import PeriodicityEmail from "./periodicityEmail";
import {StoreContext} from "../../common/struct/store";
import {AppContext} from "../../App";
import OperationMailingHistory from "./operationMailingHistory";
import {toComponentPayloads} from "./componentPayload";
import ListOtherOperationsEmailTemplates from "./otherOperations/listOtherOperationsEmailTemplates";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

export default function ListOperationEmailTemplates(props: { onEdit: (template: TEmailTemplate | null, step?: 1 | 2) => void; reload: boolean; setReload: (reload: boolean) => void; }): JSX.Element {
  const {onEdit, reload, setReload} = props;
  const {t} = useTranslation();
  const {operationId} = useParams<string>();
  const apiRef = useGridApiRef();
  const [store] = useContext<StoreContext>(AppContext);
  const operation = store.global.currentOperation;
  const [openBlockedTemplateCreation, setOpenBlockedTemplateCreation] = useState(false);
  const premiumLocked = !store.global.company.templateCreationActivated && !isSuperAdmin();
  const premiumIcon = (Icon: ElementType): ElementType => premiumLocked ? PremiumBadge : Icon;
  const premiumAction = (action: (row?: any) => void): (row?: any) => void =>
    premiumLocked ? (): void => setOpenBlockedTemplateCreation(true) : action;

  const [alerts, setAlerts] = useState<TAlert[]>([]);
  const [template, setTemplate] = useState<TEmailTemplate | null>(null);
  const [openDeleteTemplate, setOpenDeleteTemplate] = useState(false);
  const [emailStats, setEmailStats] = useState<TEmailStats[]>([]);
  const [openArchivedPanel, setOpenArchivedPanel] = useState(false);
  const [openOtherTemplatesPanel, setOpenOtherTemplatesPanel] = useState(false);
  const closeDeleteTemplate = (): void => setOpenDeleteTemplate(false);
  const [openPeriodicityEmail, setOpenPeriodicityEmail] = useState(false);
  const [resetTemplateTarget, setResetTemplateTarget] = useState<TEmailTemplate | null>(null);
  const [selectedTemplate] = useState<TEmailTemplate | undefined>(undefined);

  const closePeriodicityEmail = (): void => {
    setOpenPeriodicityEmail(false);
    setReload(true);
  };
  const [previewTemplate, setPreviewTemplate] = useState<TEmailTemplate | null>(null);
  const closePreview = (): void => setPreviewTemplate(null);
  const [historyTemplate, setHistoryTemplate] = useState<TEmailTemplate | null>(null);
  const closeHistory = (): void => setHistoryTemplate(null);

  useEffect(() => {
    if (operationId) {
      getOperationEmailStats(operationId)
        .then(stats => {
          setEmailStats(stats.perTemplate);
        })
        .catch(error => manageStringError(error, error => addErrorAlert(error, setAlerts), t));
    }
  }, [operationId]);

  const editTemplate = (template: TEmailTemplate): void => {
    onEdit(template, 1);
  };

  const editTemplateConfig = (template: TEmailTemplate): void => {
    onEdit(template, 2);
  };

  const openDeleteTemplateDialog = (template: TEmailTemplate): void => {
    setTemplate(template);
    setOpenDeleteTemplate(true);
  };

  const deleteTemplateAction = (): Promise<void> => {
    if (template == null) return Promise.resolve();
    return deleteEmailTemplate(template.id)
      .then(() => {
        setReload(true);
        addSuccessAlert(t("email_templates.actions.delete_success"), setAlerts);
      })
      .catch(error => manageStringError(error, error => addErrorAlert(error, setAlerts), t))
      .finally(closeDeleteTemplate);
  };

  const openResetTemplateDialog = (template: TEmailTemplate): void => setResetTemplateTarget(template);
  const closeResetTemplateDialog = (): void => setResetTemplateTarget(null);

  const resetTemplateAction = (): void => {
    if (resetTemplateTarget == null) return;
    resetInvitationEmailTemplate(resetTemplateTarget.id)
      .then(() => {
        setReload(true);
        addSuccessAlert(t("email_templates.actions.reset_invitation_success"), setAlerts);
      })
      .catch(error => manageStringError(error, error => addErrorAlert(error, setAlerts), t))
      .finally(closeResetTemplateDialog);
  };

  const statusColumn = (): GridEnrichedColDef => ({
    ...columnDefinition(t, "operation_email_templates", "status", 150),
    renderCell: (params): JSX.Element => {
      const stats = emailStats.find(stat => stat.id === params.row.id);
      const sent = Number(stats?.sent ?? 0);

      let label: string;
      let bgColor: string;
      let textColor: string;

      if (sent > 0) {
        label = t("operation_email_templates.status.sent");
        bgColor = "success.main";
        textColor = "white";
      } else if (params.row.newPointAdded || params.row.newParticipantAdded || params.row.beginOperationAdded) {
        label = t("operation_email_templates.status.scheduled");
        bgColor = "info.main";
        textColor = "white";
      } else {
        label = t("operation_email_templates.status.draft");
        bgColor = "ornament.dark";
        textColor = "neutral.dark";
      }

      return (
        <Chip sx={{
          backgroundColor: bgColor,
          borderRadius: "4px",
          boxShadow: 5,
          py: 0,
          px: "0px",
          color: textColor
        }}
        label={<Typography variant="body2" sx={{fontWeight: "regular"}}>{label}</Typography>} />
      );
    }
  });

  const openRateColumn = (): GridEnrichedColDef => ({
    ...columnDefinition(t, "operation_email_templates", "opened", 150, ColumnType.STRING, false),
    renderCell: (params): JSX.Element => {
      const stats = emailStats.find(stat => stat.id === params.row.id);
      const opens = Number(stats?.opens ?? 0);
      const sent = Number(stats?.sent ?? 0);
      const rate = sent > 0 ? Math.round((opens / sent) * 100) : 0;
      return <Typography variant="body2">{sent === 0 ? "-" : `${rate}%`}</Typography>;
    }
  });

  const sentColumn = (): GridEnrichedColDef => ({
    ...columnDefinition(t, "operation_email_templates", "send_count", 150, ColumnType.STRING, false),
    renderCell: (params): JSX.Element => {
      const stats = emailStats.find(stat => stat.id === params.row.id);
      const sent = Number(stats?.sent ?? 0);
      return <Typography variant="body2">{sent === 0 ? "-" : sent}</Typography>;
    }
  });
  const tagColumn = (): GridEnrichedColDef => ({
    ...columnDefinition(t, "operation_email_templates", "tag", 250, ColumnType.STRING, false),
    renderCell: (params): JSX.Element => {
      const {newPointAdded, newParticipantAdded, beginOperationAdded} = params.row;
      const tags = [];
      if (newPointAdded) tags.push(t("email_templates.config.trigger.newPoints"));
      if (newParticipantAdded) tags.push(t("email_templates.config.trigger.newParticipant"));
      if (beginOperationAdded) tags.push(t("email_templates.config.trigger.newOperation"));
      return (
        <Typography variant="body2">{tags.length > 0 ? tags.join(", ") : "-"}</Typography>
      );
    }
  });

  const duplicateTemplate = (template: TEmailTemplate): void => {
    createEmailTemplate({
      templateType: template.templateType === TEmailTemplateType.PARTICIPANT_INVITATION
        ? TEmailTemplateType.CUSTOM_TEMPLATE
        : template.templateType,
      mailjetId: template.mailjetId,
      operation: "bo/operations/" + operationId,
      name: `Copie de ${template.name}`,
      subject: template.subject ?? null,
      mailSender: template.mailSender ?? null,
      extraVariables: template.extraVariables,
      components: toComponentPayloads(template.components),
      newPointAdded: false,
      newParticipantAdded: false,
      beginOperationAdded: false,
    })
      .then(() => {
        setReload(true);
        addSuccessAlert(t("email_templates.actions.duplicate_success"), setAlerts);
      })
      .catch(error => manageStringError(error, error => addErrorAlert(error, setAlerts), t));
  }

  const navigate = useNavigate();

  const sendTemplateToParticipants = (template: TEmailTemplate): void => {
    navigate(
      `${operationUrl(PRIVATE_URL.LIST_OPERATION_PARTICIPANTS, operationId!)}?emailTemplate=${template.id}`,
      {state: {emailTemplateName: template.name}}
    );
  };

  const sendTestTemplate = (template: TEmailTemplate): void => {
    sendTestEmailForTemplate(template.id)
      .then(() => addSuccessAlert(t("email_templates.actions.send_test_email_success"), setAlerts))
      .catch(error => manageStringError(error, error => addErrorAlert(error, setAlerts), t));
  }

  const exportTemplateStats = (template: TEmailTemplate): void => {
    getExportOperationEmailStats(operationId!, template.id)
      .then(response => getExportDocument(response))
      .catch(error => manageStringError(error, error => addErrorAlert(error, setAlerts), t));
  }

  const columns = [
    columnDefinition(t, "operation_email_templates", "name", 200),
    columnDefinition(t, "operation_email_templates", "subject", 250),
    columnDefinition(t, "operation_email_templates", "updatedAt", 200, ColumnType.DATE, true, true),
    statusColumn(),
    openRateColumn(),
    sentColumn(),
    columnDefinition(t, "operation_email_templates", "periodicity", 160),
    tagColumn(),
    actionColumn(params => [
      gridAction(params, "update", t("email_templates.actions.update_custom"), EditIcon, editTemplate, false, false),
      gridAction(params, "preview", t("email_templates.actions.preview"), VisibilityIcon, (template) => setPreviewTemplate(template), false, false),
      gridAction(params, "history", t("email_templates.actions.history"), HistoryIcon, (template) => setHistoryTemplate(template), false, false),
      gridAction(params, "test", t("email_templates.actions.send_test_email"), EmailIcon, sendTestTemplate, false, false),
      gridAction(params, "update", t("email_templates.actions.update_config"), SettingsIcon, editTemplateConfig, false, false),
      ...(params.row.templateType === TEmailTemplateType.PARTICIPANT_INVITATION
        ? [gridAction(params, "reset", t("email_templates.actions.reset_invitation"), RestartAltIcon, openResetTemplateDialog, true, false)]
        : []),
      gridAction(params, "duplicate", t("email_templates.actions.duplicate"), premiumIcon(AddIcon), premiumAction(duplicateTemplate), false, false),
      gridAction(params, "send", t("email_templates.actions.send_to_participants"), premiumIcon(SendIcon), premiumAction(sendTemplateToParticipants), false, false),
      gridAction(params, "export", t("email_templates.actions.export_stats"), premiumIcon(FileDownloadIcon), premiumAction(exportTemplateStats), false, false),
      ...(params.row.templateType !== TEmailTemplateType.PARTICIPANT_INVITATION
        ? [gridAction(params, "delete", t("email_templates.actions.delete"), DeleteIcon, openDeleteTemplateDialog, false, false, true, true)]
        : []),
    ])
  ];

  const [gridColumns, setGridColumns] = useState<GridColumns>(columns);

  useEffect(() => {
    setGridColumns(columns);
  }, [emailStats]);

  const loadTemplates = (page: number, maxItems: number, search: string, order: RequestOrder, filters: RequestFilter[]): ReturnType<typeof getEmailTemplates> => {
    return getEmailTemplates(page, maxItems, search, order, [...filters, {field: 'operation', items: [{operator: RequestFilterOperator.EQUALS, value: `bo/operations/${operationId}`, isDate: false, isDatePrecise: false}]}]);
  };

  const archivedActions = <ListMenuItem key="archived-templates" label={t("email_templates.actions.archived")} icon={InventoryIcon} action={(): void => setOpenArchivedPanel(true)} />

  const otherTemplatesActions = <ListMenuItem key="other-templates" label={t("email_templates.actions.other_operations")} icon={premiumIcon(ContentCopyIcon)} action={premiumAction((): void => setOpenOtherTemplatesPanel(true))} />

  return (
    <>
      <BlockedTemplateCreationDialog open={openBlockedTemplateCreation} close={(): void => setOpenBlockedTemplateCreation(false)} />
      {previewTemplate && (
        <Panel
          title={t("email_templates.preview.preview_title")}
          size={PanelSize.HUGE}
          open={previewTemplate !== null}
          close={closePreview}>
          <EmailTemplatePreviewPanel template={previewTemplate} operationId={operationId!} addSuccess={(msg): void => addSuccessAlert(msg, setAlerts)} addError={(msg): void => addErrorAlert(msg, setAlerts)} />
        </Panel>
      )}
      {premiumLocked && (
        <PremiumFeaturesBox
          title={t("email_templates.premium_box.title")}
          features={[
            {icon: SendIcon, label: t("email_templates.premium_box.create")},
            {icon: TimelapseIcon, label: t("email_templates.premium_box.send")},
            {icon: TouchAppIcon, label: t("email_templates.premium_box.stats")}
          ]} />
      )}
      <Typography variant="body1" sx={{fontWeight: 'bold', color: 'neutral.dark', mb: 2}}>
        {t("email_templates.list_templates")}
      </Typography>
      <List
        translationPrefix="operation_email_templates"
        alerts={alerts}
        setAlerts={setAlerts}
        dialogs={[
          <DeleteDialog key="delete-template" open={openDeleteTemplate} close={closeDeleteTemplate}
            title={t("email_templates.actions.delete_title")}
            warning={t("email_templates.actions.delete_warning")}
            deleteElement={deleteTemplateAction} />,
          <RestoreDialog key="reset-template" open={resetTemplateTarget !== null} close={closeResetTemplateDialog}
            title={t("email_templates.actions.reset_invitation_title")}
            info={t("email_templates.actions.reset_invitation_confirm")}
            confirmLabel={t("email_templates.actions.reset_invitation_validate")}
            restoreElement={resetTemplateAction} />
        ]}
        columns={gridColumns}
        apiRef={apiRef}
        reload={reload}
        setReload={setReload}
        enableSelection={false}
        loadRows={loadTemplates}
        height="100%"
        emptyListIcon={emptyTransactions}
        sx={{width: "100%", mb: 3, minHeight: 400}}
        enableToolbarSearch={true}
        listActions={[archivedActions, otherTemplatesActions]}
      />
      {openArchivedPanel && <Panel title={t("email_templates.archived.title")} size={PanelSize.BIG} open={openArchivedPanel} close={(): void => setOpenArchivedPanel(false)}>
        <ListArchivedEmailTemplate operationId={operationId!} setReloadParent={setReload} />
      </Panel>}
      {openOtherTemplatesPanel && <Panel title={t("email_templates.other_operations.title")} size={PanelSize.BIG} open={openOtherTemplatesPanel} close={(): void => setOpenOtherTemplatesPanel(false)}>
        <ListOtherOperationsEmailTemplates operationId={operationId!} setReloadParent={setReload} />
      </Panel>}
      {openPeriodicityEmail && <PeriodicityEmail openDialog={openPeriodicityEmail} closeDialog={closePeriodicityEmail} templateMail={selectedTemplate} operation={operation} />}

      {historyTemplate && <Panel title={t("email_templates.history.title", {templateName: historyTemplate.name})} size={PanelSize.HUGE} open={historyTemplate !== null} close={closeHistory}>
        <OperationMailingHistory operation={{id: operationId!} as TOperation} setError={(msg): void => addErrorAlert(msg, setAlerts)} setSuccess={(msg): void => addSuccessAlert(msg, setAlerts)} templateId={historyTemplate.id} />
      </Panel>}
    </>
  );
}
